import { FMDPStorage } from "../../../shared/helper";
import {
  getIgnitionStatus,
  getTimeGap,
  valueFormatter,
  spreadDeviceDataObject,
  displayKeyName,
  getValueByNestedKey,
} from "../utils";
import moment from "moment";
import * as dataConfigJson from "../data-config.json";
import { useSelector } from "react-redux";
import { getLocalizedString } from "../../../shared/translation";
import Scrollbars from "react-custom-scrollbars-2";
import { useMemo, useRef, useEffect } from "react";

export const HeaderDetails = ({
  realTimedata,
  deviceId,
  deviceData,
  type,
  selectedVehiclePlate,
  // Canonical telemetry getters from `useVehicleTelemetry` (spec
  // `vehicle-realtime-data-consistency`, task 3.9). Absent for the Devices
  // containers, which keep their existing behavior.
  telemetry = null,
}) => {
  const latestDataRef = useRef(null);
  const variableData = useSelector((state) => state.common.variableDatas);

  // Reset latestDataRef when selected vehicle/device changes
  useEffect(() => {
    latestDataRef.current = null;
  }, [selectedVehiclePlate, deviceId]);

  const realTimeDataSource = useMemo(() => {
    const currentData =
      type === "Vehicles" ? realTimedata[selectedVehiclePlate] : realTimedata[deviceId];
    const identifier = type === "Vehicles" ? selectedVehiclePlate : deviceId;

    // Only update ref if we have data and it's for the current vehicle/device
    if (currentData && latestDataRef.current?.identifier === identifier) {
      latestDataRef.current = { ...currentData, identifier };
    } else if (currentData) {
      // If identifier changed or no previous data, set new data
      latestDataRef.current = { ...currentData, identifier };
    }

    return latestDataRef.current;
  }, [type, realTimedata, selectedVehiclePlate, deviceId]);

  /**
   * Live summary movement. When the canonical store knows the plate, speed and
   * direction come from the single reconciled `movement` object the sidebar and
   * live table also read, so the three surfaces cannot disagree (#119201).
   * Otherwise the previous realtime/device-data fallback is kept unchanged.
   */
  const canonicalMovement =
    type === "Vehicles"
      ? (telemetry?.getCurrentMovementByPlateNumber?.(selectedVehiclePlate) ?? null)
      : null;
  const canonicalIgnition =
    type === "Vehicles"
      ? (telemetry?.getCurrentTelemetryByPlateNumber?.(selectedVehiclePlate)?.ignition ?? null)
      : null;

  const vehicleDetails = useMemo(() => {
    // For each field, use realtime data if available, otherwise fall back to device data
    return {
      speed:
        canonicalMovement?.speed ??
        realTimeDataSource?.velocity?.speed ??
        deviceData?.velocity?.v?.speed,
      direction:
        canonicalMovement?.direction ??
        realTimeDataSource?.velocity?.direction ??
        deviceData?.velocity?.v?.direction,
      serverTime:
        realTimeDataSource?.server_time ?? deviceData?.server_time?.v ?? deviceData?.server_time,
      ignition: canonicalIgnition
        ? canonicalIgnition.value
        : (realTimeDataSource?.ignition ?? deviceData?.ignition?.v),
    };
  }, [realTimeDataSource, deviceData, canonicalMovement, canonicalIgnition]);

  const renderListItem = (key, value) => {
    if (value !== null && key !== "cell_info" && !key.startsWith("_")) {
      // Get realtime value if available
      const realtimeValue = realTimeDataSource
        ? getValueByNestedKey(realTimeDataSource, key)
        : null;

      // Use realtime value if available, otherwise use device data
      const finalValue = realtimeValue ?? value;

      // Skip if both realtime and device data are null/undefined
      if (finalValue == null) return null;

      let ItemValues = valueFormatter(key, finalValue, variableData);

      // Skip if the formatted value is empty or just whitespace
      if (!ItemValues?.trim()) return null;

      return ItemValues;
    }
    return null;
  };

  /**
   * Formats one canonical/fallback field. `renderListItem` re-reads the raw
   * realtime packet, which would undo the canonical reconciliation, so it is only
   * consulted when the store has no observation for this field.
   */
  const formatMovementField = (key, value, isCanonicalValue) => {
    if (isCanonicalValue) return valueFormatter(key, value, variableData);
    return renderListItem(key, value);
  };

  const getDirection = () => {
    const dir =
      formatMovementField(
        "velocity.direction",
        vehicleDetails.direction,
        canonicalMovement?.direction != null
      ) || vehicleDetails.direction;
    return dir?.substring(0, dir?.indexOf("°")) || dir; // Remove any trailing characters from '°'
  };

  return (
    <>
      <li className="details-block_summary_list_item">
        <strong>{getLocalizedString("ignition", "Ignition")} :</strong>{" "}
        {getIgnitionStatus(vehicleDetails.ignition)}
      </li>
      <li className="details-block_summary_list_item">
        <strong>{getLocalizedString("speed", "Speed")} :</strong>{" "}
        {vehicleDetails.speed == 0 || vehicleDetails.speed
          ? `${
              formatMovementField(
                "velocity.speed",
                vehicleDetails.speed,
                canonicalMovement?.speed != null
              ) || (vehicleDetails.speed * 3.6).toFixed(1) + " km/h"
            }`
          : "Not Available"}
      </li>
      <li className="details-block_summary_list_item">
        <strong>{getLocalizedString("direction", "Direction")} :</strong>{" "}
        {vehicleDetails.direction == 0 || vehicleDetails.direction
          ? `${getDirection()}°`
          : "Not Available"}
      </li>
      <li
        className="details-block_summary_list_item details-block_device-imei"
        title={deviceData?.source_id}
      >
        <strong>{getLocalizedString("device_imei", "Device IMEI")} :</strong>{" "}
        {deviceData?.source_id}
      </li>
      {type === "Devices" && (
        <li
          className="details-block_summary_list_item details-block_device-imei"
          title={deviceData?.plate_number?.v}
        >
          <strong>
            {getLocalizedString("plate_vehicle_plate_number", "Vehicle Plate Number")} :
          </strong>{" "}
          {deviceData?.plate_number?.v || "NA"}
        </li>
      )}
    </>
  );
};

export const ListDetails = ({
  clientConfig = null,
  realTimedata = [],
  deviceData,
  deviceId,
  currentTab,
  selectedVehiclePlate,
  type,
}) => {
  const variableData = useSelector((state) => state.common.variableDatas);
  const latestDataRef = useRef(null);

  // Reset latestDataRef when selected vehicle/device changes
  useEffect(() => {
    latestDataRef.current = null;
  }, [selectedVehiclePlate, deviceId]);

  const realTimeDataSource = useMemo(() => {
    const currentData =
      type === "Vehicles" ? realTimedata[selectedVehiclePlate] : realTimedata[deviceId];
    const identifier = type === "Vehicles" ? selectedVehiclePlate : deviceId;

    // Only update ref if we have data and it's for the current vehicle/device
    if (currentData && latestDataRef.current?.identifier === identifier) {
      latestDataRef.current = { ...currentData, identifier };
    } else if (currentData) {
      // If identifier changed or no previous data, set new data
      latestDataRef.current = { ...currentData, identifier };
    }

    return latestDataRef.current;
  }, [type, realTimedata, selectedVehiclePlate, deviceId]);

  const clientsData = useMemo(() => {
    return JSON.parse(JSON.stringify(dataConfigJson))?.clients;
  }, []);

  const selectedClientName = FMDPStorage.get("selected-client");

  const { view_all, selectedFields } = useMemo(() => {
    let view_all = true;
    if (clientConfig?.tracking?.web?.views?.health?.view_all != null) {
      view_all = clientConfig?.tracking?.web?.views?.health?.view_all;
    }

    let selectedFields = [];
    if (!view_all) {
      selectedFields =
        Object.keys(clientConfig).length &&
        Object.values(clientConfig?.tracking?.mobile?.views?.health?.fields);
    }

    return { view_all, selectedFields };
  }, [clientConfig]);

  const spreadDeviceDataItems = useMemo(() => {
    return Object.entries(spreadDeviceDataObject(deviceData));
  }, [deviceData]);

  const renderListItem = (key, value) => {
    if (value !== null && key !== "cell_info" && !key.startsWith("_")) {
      // Get realtime value if available
      const realtimeValue = realTimeDataSource
        ? getValueByNestedKey(realTimeDataSource, key)
        : null;

      // Use realtime value if available, otherwise use device data
      const finalValue = realtimeValue ?? value;

      // Skip if both realtime and device data are null/undefined
      if (finalValue == null) return null;

      let ItemValues = valueFormatter(key, finalValue, variableData);

      // Skip if the formatted value is empty or just whitespace
      if (!ItemValues?.trim()) return null;

      return (
        <li key={key} className="details-block_other-data_item">
          <strong
            title={displayKeyName(variableData, key)}
            className="details-block_other-data_item_label"
          >
            {getLocalizedString(key, displayKeyName(variableData, key))}
          </strong>
          <span title={ItemValues} className="details-block_other-data_item_value">
            {ItemValues}
          </span>
        </li>
      );
    }
    return null;
  };

  return view_all ? (
    <Scrollbars>
      <ul className={`qi-list-view_list details-block_data details-block_other-data ${currentTab}`}>
        {spreadDeviceDataItems.map(([key, value]) => renderListItem(key, value))}
      </ul>
    </Scrollbars>
  ) : (
    <Scrollbars>
      <ul className={`qi-list-view_list details-block_data details-block_other-data ${currentTab}`}>
        {spreadDeviceDataItems.map(([key, value]) => {
          if (selectedFields.includes(key)) {
            return renderListItem(key, value);
          }
          return null;
        })}
      </ul>
    </Scrollbars>
  );
};
